import React, { useState } from 'react';
import { useEventBooking } from '../../hooks/useEventBooking';
import { useTranslation } from 'react-i18next';
import { ImageEventType, EventType, CityType, ShowroomType } from '../../types';
import { MultiStepForm, Step } from '../MultiStepForm/MultiStepForm';
import { UserInfoStep } from '../MultiStepForm/steps/UserInfoStep';
import { DateTimeStep } from '../MultiStepForm/steps/DateTimeStep';
import { ConfirmationStep } from '../MultiStepForm/steps/ConfirmationStep';
import { CompletedStep } from '../MultiStepForm/steps/CompletedStep';
import { AdditionalInfoStep } from '../MultiStepForm/steps/AdditionalInfoStep';
import { BookingConfirmation } from '../BookingConfirmation/BookingConfirmation';
import formIconCeu from '../../assets/icons/form-icon-ceu.svg';
import formIconInfo from '../../assets/icons/form-icon-info.svg';
import formIconConfirmNotReached from '../../assets/icons/form-icon-confirm-not-reached.svg';
import formIconConfirmReached from '../../assets/icons/form-icon-confirm-reached.svg';
import dropdownCarret from '../../assets/icons/dropdown-carret.svg';
import './EventCard.scss';

export type CardEvent = {
  id: string;
  remoteObjectId: string;
  title: string;
  type: EventType;
  duration: string;
  description: string;
  date: string;
  time: string;
  location: string;
  city: CityType;
  formType?: string;
  showroom: ShowroomType;
  imageUrl: string;
  imageEventType: ImageEventType;
};

type FormVariant = 'event' | 'appointment';

interface EventCardProps {
  event: CardEvent;
  formVariant?: FormVariant;
  onClick?: () => void;
  isBooked?: boolean;
  isAttended?: boolean;
}

export const EventCard: React.FC<EventCardProps> = ({ event, formVariant = 'event', onClick, isBooked = false, isAttended = false }) => {
  const { t: eventT } = useTranslation('events');
  const { t: appointmentT } = useTranslation('appointments');
  const [isExpanded, setIsExpanded] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const [formData, setFormData] = useState<Record<string, any>>({});
  const descriptionRef = React.useRef<HTMLParagraphElement>(null);
  const { bookEvent, isEventBooked } = useEventBooking();

  // Get booking status from props or check with isEventBooked
  const isEventCurrentlyBooked = isBooked || (event.remoteObjectId && isEventBooked(event.remoteObjectId));

  const handleStepComplete = (stepData: Record<string, any>) => {
    setFormData(prev => ({ ...prev, ...stepData }));
  };

  const handleBookingClick = () => {
    if (isEventCurrentlyBooked) {
      // If already booked, don't allow booking again
      return;
    }
    
    if (onClick) {
      onClick();
    } else {
      setShowBookingForm(true);
    }
  };


  const handleCloseForm = () => {
    setShowBookingForm(false);
  };

  const handleFormComplete = async (data: Record<string, any>) => {
    if (formVariant === 'event') {
      try {
        // Build the payload based on event type
        const payload: Record<string, any> = {
          eventId: event.remoteObjectId,
          // Required fields from EventBookingRequest
          firstname: data.firstName || '',
          lastname: data.lastName || '',
          email: data.email || '',
          phone: data.phone || '',
          company: data.company || '',
        };

        // Always add jobTitle for CEU events (even if empty)
        if (event.formType === 'ceu') {
          payload.jobTitle = data.jobTitle || '';
        } else if (data.jobTitle) {
          // For other event types, only add if it has a value
          payload.jobTitle = data.jobTitle;
        }

        // Add optional fields with correct field mappings
        if (data.foodAllergies) {
          payload.foodDietaryRestriction = data.foodAllergies;
        }
        if (data.guestName) {
          payload.guestName = data.guestName;
        }
        if (data.productsInterested) {
          payload.productsInterest = data.productsInterested;
        }
        if (data.retailer) {
          payload.applianceRetailer = data.retailer;
        }

        // Add notes/additionalComments - CEU uses notes, others use additionalComments
        if (data.notes) {
          payload.additionalComments = data.notes;
        } else if (data.additionalComments) {
          payload.additionalComments = data.additionalComments;
        }

        // Fields for CEU events
        if (data.creditReporting) {
          payload.requireCreditReport = data.creditReporting;
        }

        // Fields for Public Ownership Experience
        if (data.productsOwned) {
          payload.productsRelevant = data.productsOwned;
        }

        // Fields for Dealer Training
        if (data.trainingGoals) {
          payload.expectation = data.trainingGoals;
        }
        if (data.previousTraining) {
          payload.alreadyAttented = data.previousTraining;
        }
        if (data.privateSession) {
          payload.interestForScheduling = data.privateSession;
        }
        if (data.currentBrands) {
          payload.brandSelled = data.currentBrands;
        }
        if (data.companyName) {
          payload.companyName = data.companyName;
        }
        if (data.location) {
          payload.location = data.location;
        }

        // Project address fields for Private Introduction Class and Public Ownership Experience
        if (data.projectAddressStreet) {
          payload.projectAddressStreet = data.projectAddressStreet;
        }
        if (data.projectAddressCity) {
          payload.projectAddressCity = data.projectAddressCity;
        }
        if (data.projectAddressPostalCode) {
          payload.projectAddressPostalCode = data.projectAddressPostalCode;
        }
        if (data.projectAddressProvince) {
          payload.projectAddressProvince = data.projectAddressProvince;
        }

        // Trade professionals field for Private Introduction Class
        if (data.tradeProfessionals) {
          payload.tradeProfessionals = data.tradeProfessionals;
        }

        await bookEvent(payload as any);
        setShowConfirmation(true);
        setShowBookingForm(false);
      } catch (error) {
        console.error('Failed to book event:', error);
      }
    }
  };

  const handleCloseConfirmation = () => {
    setShowConfirmation(false);
  };

  // Helper to determine if additional info step should be shown
  const shouldShowAdditionalInfo = (formType: string | undefined): boolean => {
    if (!formType) return false;
    // Exclude event types, special-event, and unknown-appointment
    return !formType.startsWith('event-') && formType !== 'special-event' && formType !== 'unknown-appointment';
  };

  // Helper to get appointment title with fallback
  const getAppointmentTitle = (title: string): string => {
    if (!title) return '';
    
    // Convert title to camelCase for translation key
    const translationKey = `appointmentTitles.${title.toLowerCase().split(' ').map((word, index) => 
      index === 0 ? word : word[0].toUpperCase() + word.slice(1)
    ).join('')}`;
    
    // Try to get translation
    const translated = appointmentT(translationKey);
    
    // If translation key is returned as-is (not found), use the original title
    if (translated === translationKey || translated.startsWith('appointmentTitles.')) {
      return title.toUpperCase();
    }
    
    return translated.toUpperCase();
  };

  const bookingSteps: Step[] = (formVariant as FormVariant) === 'event' ? [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: event.title,
      isCompleted: true,
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'user',
      icon: { src: formIconInfo, alt: 'User Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={(data) => {
          handleStepComplete(data);
        }}
        initialData={formData}
        formType={event.formType}
      />,
    },
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: eventT('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep eventData={event} initialData={formData} onNext={handleStepComplete} variant="event" />
    }
  ] : [
    {
      id: 'event',
      icon: { src: formIconCeu, alt: 'Event Icon' },
      title: getAppointmentTitle(event.title),
      Component: <CompletedStep onNext={() => {}} />
    },
    {
      id: 'dateTime',
      icon: { src: formIconInfo, alt: 'DateTime Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.dateTime'),
      Component: <DateTimeStep onNext={handleStepComplete} initialData={formData} />
    },
    {
      id: 'userInfo',
      icon: { src: formIconInfo, alt: 'User Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.yourInformation'),
      Component: <UserInfoStep
        onNext={(data) => {
          handleStepComplete(data);
        }}
        initialData={formData}
        formType={event.formType}
      />,
    },
    // Conditionally include Additional Info step
    ...(shouldShowAdditionalInfo(event.formType) ? [{
      id: 'additionalInfo',
      icon: { src: formIconInfo, alt: 'Additional Info Icon' },
      title: (formVariant === 'event' ? eventT : appointmentT)('form.steps.additionalInfo'),
      Component: <AdditionalInfoStep onNext={handleStepComplete} initialData={formData} formType={event.formType || ''} />
    }] : []),
    {
      id: 'confirm',
      icon: { 
        src: (isReached: boolean) => isReached ? formIconConfirmReached : formIconConfirmNotReached,
        alt: 'Confirmation Icon'
      },
      title: appointmentT('form.steps.reviewAndConfirm'),
      Component: <ConfirmationStep eventData={event} initialData={formData} onNext={handleStepComplete} variant="appointment" />
    }
  ];

  React.useEffect(() => {
    if (descriptionRef.current) {
      const element = descriptionRef.current;
      const hasOverflow = element.scrollHeight > element.clientHeight;
      setHasOverflow(hasOverflow);
    }
  }, [event.description]);

  return (
    <div className={`event-card-page ${(isBooked || isAttended) ? 'event-card-page--booked' : ''}`}>
      <img src={event.imageUrl} alt={event.title} className="event-card-page__image" />
      <div className="event-card-page__content">
        <div className="event-card-page__header">
          <div className="event-card-page__date-time">
            <span>{event.date === 'Available' ? appointmentT('available') : event.date}</span>
            <span className="event-card-page__showroom">{event.showroom}</span>
            <span className="event-card-page__duration">
              <span>{eventT('eventCard.duration', { duration: event.duration })}</span>
            </span>
          </div>
          <span className="event-card-page__type">{eventT(`eventType.${event.type === 'In-Person' ? 'inPerson' : 'virtual'}`)}</span>
        </div>
        <div className="event-card-page__title-row">
          <h3 className="event-card-page__title">{event.title}</h3>
          <button 
            className="event-card-page__info"
            onClick={() => setShowTooltip(!showTooltip)}
            aria-label="Show more information"
          >
            {showTooltip && (
              <div className="event-card-page__tooltip">
                <p>{event.description}</p>
                <div className="event-card-page__location">
                  <span>{event.location}</span>
                  <span>{event.city}</span>
                  <span>{event.showroom}</span>
                </div>
              </div>
            )}
          </button>
        </div>
        <div className="event-card-page__description-container">
          <div className="event-card-page__description-row">
            <p ref={descriptionRef} className={`event-card-page__description ${isExpanded ? 'expanded' : ''}`}>
              {event.description}
            </p>
            {hasOverflow && (
              <button
                className="event-card-page__expand"
                onClick={() => setIsExpanded(!isExpanded)}
                aria-label={isExpanded ? eventT('eventCard.showLess') : eventT('eventCard.showMore')}
              >
                <img 
                  src={dropdownCarret} 
                  alt=""
                  className={'event-card-page__expand-icon ' + (isExpanded ? 'rotated' : '')}
                />
              </button>
            )}
          </div>
        </div>
        <button 
          className={`event-card-page__book ${(isBooked || isAttended || isEventCurrentlyBooked) ? 'event-card-page__book--booked' : ''}`}
          onClick={handleBookingClick}
          disabled={Boolean(isBooked || isAttended || isEventCurrentlyBooked)}
        >
          <span>
            {(isBooked || isAttended) ? eventT('eventCard.booked') :
             eventT('eventCard.bookButton')}
          </span>
          {!isBooked && !isAttended && <span className="event-card-page__book-arrow">→</span>}
        </button>

        {showBookingForm && (
          <MultiStepForm
            steps={bookingSteps}
            initialStep={1}
            onClose={handleCloseForm}
            onComplete={handleFormComplete}
            variant="event"
          />
        )}

        {showConfirmation && (
          <BookingConfirmation onClose={handleCloseConfirmation} />
        )}
      </div>
    </div>
  );
};
